import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Pencil, Save, X, Package, ExternalLink, UploadCloud, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import RichTextEditor from "../../components/RichTextEditor";
import RichTextRenderer from "../../components/RichTextRenderer";

const slugifyPack = (value = "") =>
  value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function AdminPacks() {
  const [packs, setPacks] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [editing, setEditing] = useState(null); // pack object being edited
  const [form, setForm] = useState(emptyForm());
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadError, setUploadError] = useState("");

  function emptyForm() {
    return {
      title: "", slug: "", short_description: "", long_description: "",
      price_eur: 0, course_ids: [], cover_image: "",
      affiliate_commission_pct: 20, is_published: true, badge: "",
      seo_title: "", seo_description: "",
    };
  }

  const load = async () => {
    const [pr, cr] = await Promise.all([
      api.get("/packs/admin/all"),
      api.get("/courses/admin/all"),
    ]);
    setPacks(pr.data);
    setAllCourses(cr.data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm(emptyForm()); setUploadError(""); };
  const startEdit = (p) => {
    setEditing(p.pack_id);
    setUploadError("");
    setForm({
      title: p.title, slug: p.slug || "", short_description: p.short_description,
      long_description: p.long_description || "", price_eur: p.price_eur,
      course_ids: p.course_ids || [], cover_image: p.cover_image || "",
      affiliate_commission_pct: p.affiliate_commission_pct ?? 20,
      is_published: p.is_published !== false, badge: p.badge || "",
      seo_title: p.seo_title || "", seo_description: p.seo_description || "",
    });
  };
  const cancel = () => { setEditing(null); setForm(emptyForm()); setUploadError(""); };

  const save = async () => {
    const body = {
      ...form,
      slug: slugifyPack(form.slug || form.title),
      price_eur: parseFloat(form.price_eur) || 0,
      affiliate_commission_pct: parseFloat(form.affiliate_commission_pct) || 0,
    };
    if (editing === "new") await api.post("/packs", body);
    else await api.patch(`/packs/${editing}`, body);
    cancel(); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce pack ?")) return;
    await api.delete(`/packs/${id}`);
    await load();
    window.location.reload();
  };

  const toggleCourse = (cid) => {
    setForm((f) => ({
      ...f,
      course_ids: f.course_ids.includes(cid) ? f.course_ids.filter((x) => x !== cid) : [...f.course_ids, cid],
    }));
  };
  const uploadCoverImage = async (file) => {
    if (!file) return;
    setUploadError("");

    if (!file.type?.startsWith("image/")) {
      setUploadError("Merci de sélectionner une image valide (JPG, PNG ou WebP).");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("context", "pack");
    formData.append("seo_slug", form.slug || "");
    formData.append("seo_title", form.title || "");

    try {
      setUploadingCover(true);
      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, cover_image: data.url || data.secure_url || "" }));
    } catch (err) {
      setUploadError(err?.response?.data?.detail || "Impossible d’uploader l’image du pack pour le moment.");
    } finally {
      setUploadingCover(false);
    }
  };


  return (
    <div className="max-w-6xl mx-auto px-4 py-12" data-testid="admin-packs-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="label">Admin</p>
          <h1 className="font-heading text-3xl font-black flex items-center gap-2"><Package className="w-7 h-7 text-brand-orange" /> Packs de formations</h1>
        </div>
        {!editing && <button onClick={startCreate} className="btn-primary" data-testid="admin-create-pack-btn"><Plus className="w-4 h-4" /> Nouveau pack</button>}
      </div>

      {editing && (
        <div className="card p-6 mb-6 space-y-4" data-testid="admin-pack-form">
          <h2 className="font-heading text-xl font-bold">{editing === "new" ? "Créer un pack" : "Éditer le pack"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Titre du pack</label>
              <input
                className="input"
                placeholder="Titre du pack"
                value={form.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setForm((current) => ({
                    ...current,
                    title,
                    slug: current.slug ? current.slug : slugifyPack(title),
                  }));
                }}
                data-testid="pack-title-input"
              />
            </div>
            <div>
              <label className="label">Slug SEO du pack</label>
              <input
                className="input"
                placeholder="ex: accompagnement-dirigeant-recrutement"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugifyPack(e.target.value) })}
                data-testid="pack-slug-input"
              />
              <p className="text-xs text-gray-500 mt-1">URL publique : /packs/{form.slug || slugifyPack(form.title) || "mon-pack"}</p>
            </div>
          </div>
          <RichTextEditor value={form.short_description} onChange={(value) => setForm({ ...form, short_description: value })} placeholder="Description courte" minHeight={120} />
          <RichTextEditor value={form.long_description} onChange={(value) => setForm({ ...form, long_description: value })} placeholder="Description longue (optionnelle)" minHeight={160} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className="label">Prix EUR</label>
              <input className="input" type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} data-testid="pack-price-input" />
            </div>
            <div>
              <label className="label">Commission affiliés (%)</label>
              <input className="input" type="number" step="0.01" min="0" max="100" value={form.affiliate_commission_pct} onChange={(e) => setForm({ ...form, affiliate_commission_pct: e.target.value })} data-testid="pack-commission-input" />
            </div>
            <div>
              <label className="label">Badge (Best-seller, Nouveauté...)</label>
              <input className="input" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} placeholder="Ex: Best-seller" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="label">Image de couverture du pack</label>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 items-center">
              <input
                className="input"
                placeholder="URL de la couverture"
                value={form.cover_image}
                onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                data-testid="pack-cover-input"
              />
              <label className="btn-ghost cursor-pointer justify-center">
                {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                {uploadingCover ? "Upload..." : "Uploader une image"}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadCoverImage(e.target.files?.[0])}
                  disabled={uploadingCover}
                  data-testid="pack-cover-upload"
                />
              </label>
            </div>
            {form.cover_image && (
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">
                <img src={form.cover_image} alt="Aperçu couverture du pack" className="w-32 h-20 object-contain rounded-xl border border-white shadow-sm bg-white" />
                <p className="text-xs text-emerald-700 break-all">Image hébergée durablement : {form.cover_image}</p>
              </div>
            )}
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Meta title SEO du pack</label>
              <input
                className="input"
                placeholder="Titre SEO affiché par Google"
                value={form.seo_title}
                onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                maxLength={90}
              />
              <p className="text-xs text-gray-500 mt-1">Recommandé : 50 à 60 caractères.</p>
            </div>
            <div>
              <label className="label">Meta description SEO du pack</label>
              <textarea
                className="input min-h-[96px]"
                placeholder="Description SEO affichée par Google"
                value={form.seo_description}
                onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                maxLength={180}
              />
              <p className="text-xs text-gray-500 mt-1">Recommandé : 150 à 160 caractères.</p>
            </div>
          </div>
          <div>
            <label className="label mb-2 block">Formations incluses ({form.course_ids.length})</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-2 bg-emerald-50/50 rounded-lg" data-testid="pack-courses-picker">
              {allCourses.map((c) => (
                <label key={c.course_id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${form.course_ids.includes(c.course_id) ? "bg-brand-green text-white" : "bg-white hover:bg-emerald-100"}`}>
                  <input type="checkbox" checked={form.course_ids.includes(c.course_id)} onChange={() => toggleCourse(c.course_id)} className="w-4 h-4" data-testid={`pack-course-toggle-${c.course_id}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm line-clamp-1">{c.title}</p>
                    <p className={`text-xs ${form.course_ids.includes(c.course_id) ? "text-white/80" : "text-gray-500"}`}>{c.price_eur}€</p>
                  </div>
                </label>
              ))}
            </div>
            {form.course_ids.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                Prix unitaire total : <strong>{allCourses.filter((c) => form.course_ids.includes(c.course_id)).reduce((s, c) => s + (parseFloat(c.price_eur) || 0), 0).toFixed(2)}€</strong> · Économie pack : <strong className="text-brand-green">{(allCourses.filter((c) => form.course_ids.includes(c.course_id)).reduce((s, c) => s + (parseFloat(c.price_eur) || 0), 0) - (parseFloat(form.price_eur) || 0)).toFixed(2)}€</strong>
              </p>
            )}
          </div>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /> Publié</label>
          <div className="flex gap-2">
            <button onClick={save} disabled={!form.title || !form.short_description || !form.price_eur} className="btn-primary" data-testid="pack-save-btn"><Save className="w-4 h-4" /> Enregistrer</button>
            <button onClick={cancel} className="btn-ghost"><X className="w-4 h-4" /> Annuler</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {packs.length === 0 && !editing && <p className="card p-10 text-center text-gray-500">Aucun pack pour le moment. Crée ton premier pack.</p>}
        {packs.map((p) => (
          // Contenu pleine largeur, actions sur leur propre ligne en mobile.
          <div key={p.pack_id} className="card p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4" data-testid={`admin-pack-row-${p.pack_id}`}>
            <div className="flex min-w-0 flex-1 items-start gap-3">
              {p.cover_image && <img src={p.cover_image} alt="" className="w-20 h-14 shrink-0 rounded object-cover" />}
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold leading-snug line-clamp-2 break-words">{p.title}</p>
                {p.badge && <span className="badge orange mt-1 inline-flex text-xs">{p.badge}</span>}
                <RichTextRenderer html={p.short_description} className="mt-1 text-sm text-gray-500 line-clamp-1" />
                <p className="mt-1 text-xs text-gray-400">{(p.course_ids || []).length} formations · {p.price_eur}€ · commission {p.affiliate_commission_pct}%</p>
                <p className="truncate font-mono text-xs text-gray-400">/packs/{p.slug || p.pack_id}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-emerald-100 pt-3 lg:shrink-0 lg:border-t-0 lg:pt-0">
              <button onClick={() => startEdit(p)} className="btn-ghost" data-testid={`admin-edit-pack-${p.pack_id}`}><Pencil className="w-4 h-4" /> Éditer</button>
              <Link to={`/packs/${p.slug || p.pack_id}`} className="btn-ghost" target="_blank" title="Voir la page publique"><ExternalLink className="w-4 h-4" /></Link>
              <button onClick={() => remove(p.pack_id)} className="ml-auto p-2 text-red-500 hover:text-red-700 lg:ml-0" title="Supprimer" data-testid={`admin-delete-pack-${p.pack_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
